import { useCallback, useEffect, useMemo } from 'react';

import { useAreaUnitsLabel, useTranslate } from '../../../../../hooks';
import {
    BasinFeatureProperties,
    MapControlAccordionProps,
    MSatPickInfo,
    OGILayerIds,
    OGITileFeatureProperties
} from '../../../../../types';
import { analytics } from '../../../../../utils';
import { InfrastructureModalFooter } from '../../Footers';
import { GeoFeatureModalHeader } from '../../Headers';
import { DataRowsModalBody, DataSkeleton, DrawerCardContainer } from '../../Layout';
import { useGetOgiMetadataQuery } from '../../../../../reducers';

export const BasinCard = ({
    info
}: {
    info: MSatPickInfo<GeoJSON.Geometry, OGITileFeatureProperties>;
    AccordionProps?: MapControlAccordionProps;
}) => {
    const t = useTranslate();

    const objectId = info.object.id;
    const layerId = info?.layer.id;
    const shouldFetch = layerId.includes(OGILayerIds.tileInfrastructure);

    const { data: basinMetadataList, isFetching: loadingState } = useGetOgiMetadataQuery(
        { ogimIds: [objectId] },
        { skip: !shouldFetch }
    );

    const basinMetadata = basinMetadataList?.[0] as BasinFeatureProperties;

    const metadataForAnalytics = useMemo(
        () =>
            basinMetadata && {
                name: basinMetadata.details.name
            },
        [basinMetadata]
    );
    // called from useEffect below or Accordion onChange event
    const logAnalytics = useCallback(
        () => metadataForAnalytics && analytics.openBasinDetails(metadataForAnalytics),
        [metadataForAnalytics]
    );

    // tracks user opening details *not* in an accordion
    // viewing details in accordions are tracked in onChange events
    useEffect(() => {
        logAnalytics();
    }, [basinMetadata, logAnalytics]);

    const areaUnitsLabel = useAreaUnitsLabel();

    if (loadingState) {
        return <DataSkeleton />;
    }

    const headerProps = {
        name: `${basinMetadata?.details.name} ${t('emissionsMapPage.mapModals.basin.label')}`.toUpperCase(),
        infraCategory: t('emissionsMapPage.mapModals.basin.category')
    };

    const dataRows = [
        {
            label: t('emissionsMapPage.mapModals.common.featureTypeLabel'),
            value: t('emissionsMapPage.mapModals.basin.label')
        },
        ...((basinMetadata?.details.area_km2 && [
            {
                label: t('emissionsMapPage.mapModals.common.areaLabel'),
                value: `${basinMetadata?.details.area_km2.toLocaleString(undefined, {
                    maximumSignificantDigits: 2
                })} ${areaUnitsLabel}`
            }
        ]) ||
            [])
    ];

    const sources = basinMetadata?.sources;

    return (
        <DrawerCardContainer
            HeaderItems={<GeoFeatureModalHeader {...headerProps} />}
            Body={<DataRowsModalBody dataRows={dataRows} />}
            Footer={
                sources &&
                sources.length &&
                sources.map((source) => <InfrastructureModalFooter key={source.src_id} {...source} />)
            }
        />
    );
};
